import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import './Admin.css';

const ORDER_STATES = ['solicitado', 'confirmado', 'en_preparacion', 'listo', 'entregado', 'cancelado'];
const CUSTOM_STATES = ['nuevo', 'contactado', 'presupuestado', 'confirmado', 'cerrado', 'descartado'];
const INVOICE_STATES = ['borrador', 'emitida', 'pagada', 'anulada'];
const MAIL_STATES = ['new', 'read', 'replied', 'archived'];

const ORDER_LABELS = {
  solicitado: 'Solicitado',
  confirmado: 'Confirmado',
  en_preparacion: 'En preparacion',
  listo: 'Listo',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const CUSTOM_LABELS = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  presupuestado: 'Presupuestado',
  confirmado: 'Confirmado',
  cerrado: 'Cerrado',
  descartado: 'Descartado',
};

const INVOICE_LABELS = {
  borrador: 'Borrador',
  emitida: 'Emitida',
  pagada: 'Pagada',
  anulada: 'Anulada',
};

const MAIL_LABELS = {
  new: 'Nueva',
  read: 'Leida',
  replied: 'Respondida',
  archived: 'Archivada',
};

const ORDER_COLUMNS = [
  { key: 'solicitado', title: 'Entrada', states: ['solicitado'] },
  { key: 'produccion', title: 'Obrador', states: ['confirmado', 'en_preparacion'] },
  { key: 'salida', title: 'Entrega', states: ['listo', 'entregado'] },
];

const SECTIONS = [
  { key: 'dashboard', label: 'Inicio', sub: 'Resumen operativo' },
  { key: 'orders', label: 'Pedidos', sub: 'Tablero y detalle' },
  { key: 'custom', label: 'A medida', sub: 'Briefs personalizados' },
  { key: 'catalog', label: 'Catalogo', sub: 'Productos y categorias' },
  { key: 'invoices', label: 'Facturas', sub: 'Facturas generadas' },
  { key: 'emailAccounts', label: 'Correos', sub: 'Cuentas corporativas' },
  { key: 'mailNotifications', label: 'Notificaciones', sub: 'Bandeja centralizada' },
  { key: 'calendar', label: 'Calendario', sub: 'Fechas y capacidad' },
  { key: 'settings', label: 'Ajustes', sub: 'Reglas del negocio' },
];

const EMPTY_PRODUCT = {
  slug: '',
  nombre: '',
  categoria: '',
  descripcion: '',
  precioBase: 0,
  imagenes: [],
  alergenos: [],
  permitePersonalizarTexto: false,
  maxCaracteresTexto: 20,
  opciones: [],
  destacado: false,
  activo: true,
  orden: 0,
};

const EMPTY_CATEGORY = {
  slug: '',
  nombre: '',
  descripcion: '',
  imagen: '',
  orden: 0,
  activo: true,
};

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function formatDate(value, withTime = false) {
  if (!value) return 'Sin fecha';
  const opts = withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { weekday: 'short', day: '2-digit', month: 'short' };
  return new Date(value).toLocaleString('es-ES', opts);
}

function ymd(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function phoneLink(phone, text = '') {
  const digits = String(phone || '').replace(/\D/g, '');
  const intl = digits.length === 9 ? `34${digits}` : digits;
  return `https://wa.me/${intl}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
}

function includesText(values, query) {
  if (!query.trim()) return true;
  const haystack = values.filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

function arrayToText(value) {
  return Array.isArray(value) ? value.join('\n') : '';
}

function textToArray(value) {
  return String(value || '')
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function prettyJson(value) {
  try { return JSON.stringify(value || [], null, 2); }
  catch { return '[]'; }
}

function Icon({ name }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  const paths = {
    dashboard: <><path d="M3 13h8V3H3z" /><path d="M13 21h8V11h-8z" /><path d="M13 3h8v4h-8z" /><path d="M3 21h8v-4H3z" /></>,
    orders: <><path d="M9 11h6" /><path d="M9 15h6" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M14 3v5h5" /></>,
    custom: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
    catalog: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></>,
    invoices: <><path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2V5a2 2 0 0 1 2-2z" /><path d="M9 8h6" /><path d="M9 12h6" /><path d="M9 16h4" /></>,
    emailAccounts: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    mailNotifications: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
    key: <><path d="M21 2 11 12" /><circle cx="7.5" cy="15.5" r="5.5" /><path d="m15 8 3 3" /><path d="m17 6 3 3" /></>,
    calendar: <><path d="M8 2v4" /><path d="M16 2v4" /><path d="M3 10h18" /><rect x="3" y="4" width="18" height="18" rx="2" /></>,
    settings: <><path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5z" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6V20a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-.6 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1H4a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 .6-1 1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6V4a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.22.36.43.63.6 1H20a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-.5 1z" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    refresh: <><path d="M21 12a9 9 0 0 1-15.2 6.5" /><path d="M3 12A9 9 0 0 1 18.2 5.5" /><path d="M3 3v6h6" /><path d="M21 21v-6h-6" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
    close: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
  };
  return <svg {...common}>{paths[name] || paths.dashboard}</svg>;
}

function EmptyState({ title, body }) {
  return (
    <div className="ka-empty">
      <div className="ka-empty-mark" />
      <h3>{title}</h3>
      {body && <p>{body}</p>}
    </div>
  );
}

function StatusPill({ value, custom = false, invoice = false, mail = false }) {
  const labels = mail ? MAIL_LABELS : invoice ? INVOICE_LABELS : custom ? CUSTOM_LABELS : ORDER_LABELS;
  return <span className={`ka-pill ka-pill--${value}`}>{labels[value] || value}</span>;
}

function StatCard({ label, value, tone = 'neutral', hint }) {
  return (
    <article className={`ka-stat ka-stat--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint && <small>{hint}</small>}
    </article>
  );
}

function AdminLogin({ pass, setPass, login, error, loading }) {
  return (
    <div className="ka-login-page">
      <section className="ka-login-card">
        <div className="ka-login-brand">
          <span className="ka-brand-mark">K</span>
          <div>
            <h1>Kanel Admin</h1>
            <p>Portal de obrador y pedidos</p>
          </div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); login(); }}>
          <label htmlFor="admin-password">Contrasena</label>
          <input
            id="admin-password"
            type="password"
            placeholder="Introduce la clave del panel"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete="current-password"
          />
          {error && <p className="ka-alert ka-alert--danger" role="alert">{error}</p>}
          <button className="ka-btn ka-btn--primary" type="submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </section>
    </div>
  );
}

function Shell({ active, setActive, logout, children, loading, reload }) {
  const current = SECTIONS.find((s) => s.key === active) || SECTIONS[0];
  return (
    <div className="ka-admin">
      <aside className="ka-sidebar">
        <div className="ka-sidebar-brand">
          <span className="ka-brand-mark">K</span>
          <div>
            <strong>Kanel</strong>
            <small>Bakery admin</small>
          </div>
        </div>
        <nav aria-label="Administracion Kanel">
          {SECTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={active === item.key ? 'ka-nav-item is-active' : 'ka-nav-item'}
              onClick={() => setActive(item.key)}
            >
              <Icon name={item.key === 'dashboard' ? 'dashboard' : item.key} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="ka-sidebar-foot">
          <a href="/" className="ka-link">Ver web</a>
          <button type="button" className="ka-link" onClick={logout}>Salir</button>
        </div>
      </aside>

      <main className="ka-main">
        <header className="ka-topbar">
          <div>
            <p className="ka-kicker">Kanel Bakery</p>
            <h1>{current.label}</h1>
            <span>{current.sub}</span>
          </div>
          <div className="ka-topbar-actions">
            <button type="button" className="ka-btn ka-btn--ghost" onClick={reload} disabled={loading}>
              <Icon name="refresh" />
              {loading ? 'Actualizando' : 'Actualizar'}
            </button>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

function Dashboard({ orders, customs, products, invoices, emailAccounts, mailNotifications, settings, setActive }) {
  const todayKey = ymd(new Date());
  const todayOrders = orders.filter((o) => ymd(o.fechaEntrega) === todayKey);
  const openOrders = orders.filter((o) => !['entregado', 'cancelado'].includes(o.estado));
  const pendingCustom = customs.filter((c) => ['nuevo', 'contactado', 'presupuestado'].includes(c.estado));
  const revenue = orders
    .filter((o) => o.estado !== 'cancelado')
    .reduce((sum, o) => sum + Number(o.total || 0), 0);
  const inactive = products.filter((p) => !p.activo).length;
  const pendingInvoices = invoices.filter((invoice) => !['pagada', 'anulada'].includes(invoice.estado));
  const newMail = mailNotifications.filter((item) => item.status === 'new');
  const autoReplyAccounts = emailAccounts.filter((item) => item.autoReplyEnabled);
  const nextOrders = [...openOrders].sort((a, b) => new Date(a.fechaEntrega) - new Date(b.fechaEntrega)).slice(0, 6);

  return (
    <div className="ka-stack">
      <section className="ka-stats">
        <StatCard label="Pedidos abiertos" value={openOrders.length} tone="accent" hint={`${todayOrders.length} para hoy`} />
        <StatCard label="A medida pendientes" value={pendingCustom.length} tone="warning" hint="Briefs por contestar" />
        <StatCard label="Importe solicitado" value={formatMoney(revenue)} tone="success" hint="Pedidos no cancelados" />
        <StatCard label="Facturas abiertas" value={pendingInvoices.length} tone="neutral" hint={`${invoices.length} generadas`} />
        <StatCard label="Correos nuevos" value={newMail.length} tone="warning" hint={`${emailAccounts.length} buzones · ${autoReplyAccounts.length} auto`} />
        <StatCard label="Catalogo activo" value={products.filter((p) => p.activo).length} tone="neutral" hint={`${inactive} pausados`} />
      </section>

      <section className="ka-grid ka-grid--dashboard">
        <article className="ka-panel">
          <div className="ka-panel-head">
            <div>
              <h2>Proximas entregas</h2>
              <p>Pedidos que necesitan seguimiento operativo.</p>
            </div>
            <button className="ka-btn ka-btn--soft" onClick={() => setActive('orders')}>Ver pedidos</button>
          </div>
          <div className="ka-list">
            {nextOrders.length === 0 ? (
              <EmptyState title="Sin entregas abiertas" body="Cuando entren pedidos apareceran aqui ordenados por fecha." />
            ) : nextOrders.map((order) => (
              <div key={order._id} className="ka-row">
                <div>
                  <strong>{order.codigo}</strong>
                  <span>{order.cliente?.nombre} · {formatDate(order.fechaEntrega)} · {order.ventana || 'Sin ventana'}</span>
                </div>
                <StatusPill value={order.estado} />
                <b>{formatMoney(order.total)}</b>
              </div>
            ))}
          </div>
        </article>

        <article className="ka-panel">
          <div className="ka-panel-head">
            <div>
              <h2>Reglas activas</h2>
              <p>Datos que afectan al checkout publico.</p>
            </div>
            <button className="ka-btn ka-btn--soft" onClick={() => setActive('calendar')}>Editar</button>
          </div>
          <dl className="ka-defs">
            <div><dt>Pedido minimo</dt><dd>{formatMoney(settings?.pedidoMinimo)}</dd></div>
            <div><dt>Antelacion estandar</dt><dd>{settings?.antelacionHoras?.estandar || 48} h</dd></div>
            <div><dt>Personalizados</dt><dd>{settings?.antelacionHoras?.personalizado || 72} h</dd></div>
            <div><dt>Ventanas</dt><dd>{(settings?.ventanas || []).join(', ') || 'Sin ventanas'}</dd></div>
            <div><dt>Envio</dt><dd>{formatMoney(settings?.tarifaEnvio)} · gratis desde {formatMoney(settings?.envioGratisDesde)}</dd></div>
          </dl>
        </article>
      </section>
    </div>
  );
}

function OrdersSection({ orders, onStatus }) {
  const [mode, setMode] = useState('board');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('all');
  const [date, setDate] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(() => orders.filter((o) => {
    if (state !== 'all' && o.estado !== state) return false;
    if (date && ymd(o.fechaEntrega) !== date) return false;
    return includesText([
      o.codigo, o.cliente?.nombre, o.cliente?.telefono, o.cliente?.email, o.direccion, o.notas,
      ...(o.items || []).map((i) => `${i.nombre} ${i.textoPersonalizado || ''}`),
    ], query);
  }), [orders, query, state, date]);

  const selectedOrder = selected ? filtered.find((o) => o._id === selected) || orders.find((o) => o._id === selected) : null;

  return (
    <div className="ka-stack">
      <section className="ka-tools">
        <div className="ka-search">
          <Icon name="search" />
          <input type="search" placeholder="Buscar por codigo, cliente, telefono o producto" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filtrar por estado">
          <option value="all">Todos los estados</option>
          {ORDER_STATES.map((s) => <option key={s} value={s}>{ORDER_LABELS[s]}</option>)}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Filtrar por entrega" />
        <div className="ka-segmented" aria-label="Modo de vista">
          <button className={mode === 'board' ? 'is-active' : ''} onClick={() => setMode('board')}>Tablero</button>
          <button className={mode === 'list' ? 'is-active' : ''} onClick={() => setMode('list')}>Lista</button>
        </div>
      </section>

      {mode === 'board' ? (
        <section className="ka-board">
          {ORDER_COLUMNS.map((col) => {
            const list = filtered.filter((o) => col.states.includes(o.estado));
            return (
              <article key={col.key} className="ka-board-col">
                <header>
                  <span>{col.title}</span>
                  <b>{list.length}</b>
                </header>
                <div className="ka-board-body">
                  {list.length === 0 ? <div className="ka-col-empty">Sin pedidos</div> : list.map((order) => (
                    <button key={order._id} type="button" className="ka-order-card" onClick={() => setSelected(order._id)}>
                      <div className="ka-order-head">
                        <strong>{order.codigo}</strong>
                        <StatusPill value={order.estado} />
                      </div>
                      <p>{order.cliente?.nombre} · {order.cliente?.telefono}</p>
                      <span>{formatDate(order.fechaEntrega)} · {order.ventana || 'Sin ventana'}</span>
                      <ul>
                        {(order.items || []).slice(0, 3).map((item, idx) => (
                          <li key={idx}>{item.cantidad}x {item.nombre}</li>
                        ))}
                      </ul>
                      <b>{formatMoney(order.total)}</b>
                    </button>
                  ))}
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="ka-panel">
          <DataTable
            empty="No hay pedidos con esos filtros."
            rows={filtered}
            render={(order) => (
              <button key={order._id} type="button" className="ka-table-row" onClick={() => setSelected(order._id)}>
                <span><strong>{order.codigo}</strong><small>{formatDate(order.createdAt, true)}</small></span>
                <span>{order.cliente?.nombre}<small>{order.cliente?.telefono}</small></span>
                <span>{formatDate(order.fechaEntrega)}<small>{order.ventana || 'Sin ventana'}</small></span>
                <span><StatusPill value={order.estado} /></span>
                <span className="ka-align-right"><b>{formatMoney(order.total)}</b></span>
              </button>
            )}
          />
        </section>
      )}

      {selectedOrder && (
        <OrderDetail order={selectedOrder} onClose={() => setSelected(null)} onStatus={onStatus} />
      )}
    </div>
  );
}

function OrderDetail({ order, onClose, onStatus }) {
  const msg = `Hola ${order.cliente?.nombre || ''}, te escribimos de Kanel Bakery sobre tu pedido ${order.codigo}.`;
  return (
    <aside className="ka-drawer" aria-label="Detalle del pedido">
      <div className="ka-drawer-card">
        <header>
          <div>
            <p className="ka-kicker">Pedido</p>
            <h2>{order.codigo}</h2>
          </div>
          <button className="ka-icon-btn" onClick={onClose} aria-label="Cerrar detalle"><Icon name="close" /></button>
        </header>
        <div className="ka-detail-grid">
          <div><span>Cliente</span><strong>{order.cliente?.nombre}</strong></div>
          <div><span>Telefono</span><a href={phoneLink(order.cliente?.telefono, msg)} target="_blank" rel="noreferrer">{order.cliente?.telefono}</a></div>
          <div><span>Email</span><strong>{order.cliente?.email || '-'}</strong></div>
          <div><span>Entrega</span><strong>{formatDate(order.fechaEntrega)} · {order.ventana || '-'}</strong></div>
          <div><span>Tipo</span><strong>{order.tipoEntrega}</strong></div>
          <div><span>Total</span><strong>{formatMoney(order.total)}</strong></div>
        </div>
        {order.direccion && <p className="ka-note"><b>Direccion:</b> {order.direccion}</p>}
        {order.notas && <p className="ka-note"><b>Notas:</b> {order.notas}</p>}
        <div className="ka-items">
          {(order.items || []).map((item, idx) => (
            <article key={idx}>
              <div>
                <strong>{item.cantidad}x {item.nombre}</strong>
                {item.textoPersonalizado && <span>Texto: {item.textoPersonalizado}</span>}
                {Object.keys(item.opcionesElegidas || {}).length > 0 && (
                  <small>{Object.entries(item.opcionesElegidas).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · ')}</small>
                )}
              </div>
              <b>{formatMoney(item.subtotal)}</b>
            </article>
          ))}
        </div>
        <label className="ka-field">
          <span>Estado del pedido</span>
          <select value={order.estado} onChange={(e) => onStatus(order._id, e.target.value)}>
            {ORDER_STATES.map((s) => <option key={s} value={s}>{ORDER_LABELS[s]}</option>)}
          </select>
        </label>
      </div>
    </aside>
  );
}

function CustomSection({ customs, onStatus }) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState('all');
  const [selected, setSelected] = useState('');

  const filtered = useMemo(() => customs.filter((c) => {
    if (state !== 'all' && c.estado !== state) return false;
    return includesText([c.codigo, c.cliente?.nombre, c.cliente?.telefono, c.tipoDulce, c.ocasion, c.descripcion, c.tematica, c.presupuesto], query);
  }), [customs, query, state]);
  const current = selected ? filtered.find((c) => c._id === selected) || customs.find((c) => c._id === selected) : filtered[0];

  return (
    <section className="ka-master-detail">
      <div className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Solicitudes a medida</h2>
            <p>Briefs que llegan desde el formulario personalizado.</p>
          </div>
        </div>
        <div className="ka-tools ka-tools--compact">
          <div className="ka-search"><Icon name="search" /><input type="search" placeholder="Buscar brief" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
          <select value={state} onChange={(e) => setState(e.target.value)}>
            <option value="all">Todos</option>
            {CUSTOM_STATES.map((s) => <option key={s} value={s}>{CUSTOM_LABELS[s]}</option>)}
          </select>
        </div>
        <div className="ka-ticket-list">
          {filtered.length === 0 ? <EmptyState title="Sin solicitudes" body="No hay briefs con los filtros seleccionados." /> : filtered.map((item) => (
            <button key={item._id} className={current?._id === item._id ? 'is-active' : ''} onClick={() => setSelected(item._id)}>
              <strong>{item.codigo} · {item.tipoDulce || 'Pedido especial'}</strong>
              <span>{item.cliente?.nombre} · {formatDate(item.createdAt, true)}</span>
              <StatusPill value={item.estado} custom />
            </button>
          ))}
        </div>
      </div>

      <div className="ka-panel">
        {current ? (
          <>
            <div className="ka-panel-head">
              <div>
                <h2>{current.codigo}</h2>
                <p>{current.ocasion || 'Solicitud personalizada'} · {current.personas || 'sin personas indicadas'}</p>
              </div>
              <StatusPill value={current.estado} custom />
            </div>
            <div className="ka-detail-grid">
              <div><span>Cliente</span><strong>{current.cliente?.nombre}</strong></div>
              <div><span>Telefono</span><a href={phoneLink(current.cliente?.telefono, `Hola ${current.cliente?.nombre || ''}, te escribimos de Kanel Bakery sobre tu solicitud ${current.codigo}.`)} target="_blank" rel="noreferrer">{current.cliente?.telefono}</a></div>
              <div><span>Email</span><strong>{current.cliente?.email || '-'}</strong></div>
              <div><span>Fecha evento</span><strong>{current.fechaEvento ? formatDate(current.fechaEvento) : '-'}</strong></div>
              <div><span>Entrega</span><strong>{current.tipoEntrega}{current.direccion ? ` · ${current.direccion}` : ''}</strong></div>
              <div><span>Presupuesto</span><strong>{current.presupuesto || '-'}</strong></div>
            </div>
            <p className="ka-note"><b>Idea:</b> {current.descripcion}</p>
            {current.tematica && <p className="ka-note"><b>Estilo:</b> {current.tematica}</p>}
            {current.alergenos && <p className="ka-note"><b>Alergenos:</b> {current.alergenos}</p>}
            {current.referencia && <p className="ka-note"><b>Referencia:</b> {current.referencia}</p>}
            {current.archivos?.length > 0 && (
              <div className="ka-files">
                {current.archivos.map((file, idx) => (
                  <a key={idx} href={file.url} target="_blank" rel="noreferrer">
                    {file.mime?.startsWith('image') ? <img src={file.url} alt={file.nombre || 'Referencia'} /> : <span>{file.nombre || 'Archivo'}</span>}
                  </a>
                ))}
              </div>
            )}
            <label className="ka-field">
              <span>Estado del brief</span>
              <select value={current.estado} onChange={(e) => onStatus(current._id, e.target.value)}>
                {CUSTOM_STATES.map((s) => <option key={s} value={s}>{CUSTOM_LABELS[s]}</option>)}
              </select>
            </label>
          </>
        ) : (
          <EmptyState title="Selecciona una solicitud" body="El detalle aparecera aqui." />
        )}
      </div>
    </section>
  );
}

function DataTable({ rows, render, empty }) {
  if (!rows.length) return <EmptyState title={empty} />;
  return <div className="ka-table">{rows.map(render)}</div>;
}

function InvoicesSection({ invoices, orders, onGenerate, onStatus }) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState('all');
  const [selected, setSelected] = useState('');

  const invoicedOrderIds = useMemo(() => new Set(invoices.map((invoice) => String(invoice.orderId))), [invoices]);
  const invoiceableOrders = useMemo(() => orders
    .filter((order) => order.estado !== 'cancelado' && !invoicedOrderIds.has(String(order._id)))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [orders, invoicedOrderIds]
  );

  const filtered = useMemo(() => invoices.filter((invoice) => {
    if (state !== 'all' && invoice.estado !== state) return false;
    return includesText([
      invoice.numero,
      invoice.orderCodigo,
      invoice.cliente?.nombre,
      invoice.cliente?.telefono,
      invoice.cliente?.email,
      invoice.paymentMethod,
      invoice.notes,
    ], query);
  }), [invoices, query, state]);

  const current = selected
    ? filtered.find((invoice) => invoice._id === selected) || invoices.find((invoice) => invoice._id === selected)
    : filtered[0];
  const totals = useMemo(() => filtered.reduce((acc, invoice) => {
    acc.count += 1;
    if (invoice.estado !== 'anulada') acc.total += Number(invoice.total || 0);
    if (invoice.estado === 'pagada') acc.paid += Number(invoice.total || 0);
    if (!['pagada', 'anulada'].includes(invoice.estado)) acc.pending += Number(invoice.total || 0);
    return acc;
  }, { count: 0, total: 0, paid: 0, pending: 0 }), [filtered]);

  return (
    <div className="ka-stack">
      <section className="ka-stats">
        <StatCard label="Facturas mostradas" value={totals.count} tone="neutral" />
        <StatCard label="Total facturado" value={formatMoney(totals.total)} tone="success" />
        <StatCard label="Cobrado" value={formatMoney(totals.paid)} tone="success" />
        <StatCard label="Pendiente" value={formatMoney(totals.pending)} tone="warning" />
      </section>

      <section className="ka-tools">
        <div className="ka-search">
          <Icon name="search" />
          <input type="search" placeholder="Buscar por factura, pedido o cliente" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filtrar facturas por estado">
          <option value="all">Todos los estados</option>
          {INVOICE_STATES.map((status) => <option key={status} value={status}>{INVOICE_LABELS[status]}</option>)}
        </select>
      </section>

      <section className="ka-master-detail ka-master-detail--wide">
        <div className="ka-panel">
          <div className="ka-panel-head">
            <div>
              <h2>Facturas generadas</h2>
              <p>Registros emitidos desde pedidos del ecommerce.</p>
            </div>
          </div>
          <DataTable
            empty="Todavia no hay facturas generadas."
            rows={filtered}
            render={(invoice) => (
              <button key={invoice._id} type="button" className="ka-table-row ka-table-row--invoice" onClick={() => setSelected(invoice._id)}>
                <span><strong>{invoice.numero}</strong><small>{formatDate(invoice.issuedAt, true)}</small></span>
                <span>{invoice.cliente?.nombre}<small>{invoice.orderCodigo}</small></span>
                <span><StatusPill value={invoice.estado} invoice /></span>
                <span className="ka-align-right"><b>{formatMoney(invoice.total)}</b></span>
              </button>
            )}
          />
        </div>

        <div className="ka-panel">
          {current ? (
            <InvoiceDetail invoice={current} onStatus={onStatus} />
          ) : (
            <EmptyState title="Selecciona una factura" body="El detalle de cobro aparecera aqui." />
          )}
        </div>
      </section>

      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Pedidos pendientes de factura</h2>
            <p>Genera una factura desde pedidos confirmados, en preparacion, listos o entregados.</p>
          </div>
        </div>
        <div className="ka-table">
          {invoiceableOrders.length === 0 ? (
            <EmptyState title="Sin pedidos pendientes de factura" body="Todos los pedidos activos ya tienen factura o no hay pedidos facturables." />
          ) : invoiceableOrders.slice(0, 30).map((order) => (
            <div key={order._id} className="ka-table-row ka-table-row--invoice-source">
              <span><strong>{order.codigo}</strong><small>{formatDate(order.createdAt, true)}</small></span>
              <span>{order.cliente?.nombre}<small>{order.cliente?.telefono}</small></span>
              <span><StatusPill value={order.estado} /></span>
              <span className="ka-align-right"><b>{formatMoney(order.total)}</b></span>
              <button type="button" className="ka-btn ka-btn--soft" onClick={() => onGenerate(order._id)}>Generar factura</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function InvoiceDetail({ invoice, onStatus }) {
  const [notes, setNotes] = useState(invoice.notes || '');
  const [paymentMethod, setPaymentMethod] = useState(invoice.paymentMethod || '');
  const [dueDate, setDueDate] = useState(invoice.dueDate ? ymd(invoice.dueDate) : '');

  useEffect(() => {
    setNotes(invoice.notes || '');
    setPaymentMethod(invoice.paymentMethod || '');
    setDueDate(invoice.dueDate ? ymd(invoice.dueDate) : '');
  }, [invoice]);

  const saveMeta = () => onStatus(invoice._id, {
    notes,
    paymentMethod,
    dueDate,
  });

  return (
    <div className="ka-invoice-detail">
      <div className="ka-panel-head">
        <div>
          <p className="ka-kicker">Factura</p>
          <h2>{invoice.numero}</h2>
          <p>Pedido {invoice.orderCodigo} · {formatDate(invoice.issuedAt, true)}</p>
        </div>
        <StatusPill value={invoice.estado} invoice />
      </div>

      <div className="ka-detail-grid">
        <div><span>Cliente</span><strong>{invoice.cliente?.nombre}</strong></div>
        <div><span>Telefono</span><strong>{invoice.cliente?.telefono || '-'}</strong></div>
        <div><span>Email</span><strong>{invoice.cliente?.email || '-'}</strong></div>
        <div><span>Total</span><strong>{formatMoney(invoice.total)}</strong></div>
      </div>

      <div className="ka-items">
        {(invoice.items || []).map((item, idx) => (
          <article key={idx}>
            <div>
              <strong>{item.cantidad}x {item.nombre}</strong>
              {item.descripcion && <span>{item.descripcion}</span>}
            </div>
            <b>{formatMoney(item.subtotal)}</b>
          </article>
        ))}
      </div>

      <dl className="ka-defs ka-defs--compact">
        <div><dt>Subtotal</dt><dd>{formatMoney(invoice.subtotal)}</dd></div>
        <div><dt>Envio</dt><dd>{formatMoney(invoice.gastosEnvio)}</dd></div>
        <div><dt>Impuestos</dt><dd>{invoice.taxRate || 0}% · {formatMoney(invoice.taxAmount)}</dd></div>
        <div><dt>Total</dt><dd>{formatMoney(invoice.total)}</dd></div>
      </dl>

      <div className="ka-form ka-invoice-form">
        <label className="ka-field">
          <span>Estado</span>
          <select value={invoice.estado} onChange={(e) => onStatus(invoice._id, { estado: e.target.value })}>
            {INVOICE_STATES.map((status) => <option key={status} value={status}>{INVOICE_LABELS[status]}</option>)}
          </select>
        </label>
        <div className="ka-form-grid">
          <label><span>Vencimiento</span><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></label>
          <label><span>Metodo de pago</span><input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} placeholder="Bizum, transferencia, efectivo..." /></label>
        </div>
        <label><span>Notas internas</span><textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <div className="ka-form-actions">
          <span />
          <button type="button" className="ka-btn ka-btn--primary" onClick={saveMeta}>Guardar factura</button>
        </div>
      </div>
    </div>
  );
}

function EmailAccountsSection({ accounts, onSave, onReveal }) {
  const [editing, setEditing] = useState('');
  const [form, setForm] = useState({});
  const [revealed, setRevealed] = useState({});

  const startEdit = (account) => {
    setEditing(account._id);
    setForm({
      label: account.label || '',
      purpose: account.purpose || '',
      status: account.status || 'active',
      autoReplyEnabled: Boolean(account.autoReplyEnabled),
      autoReplyTemplate: account.autoReplyTemplate || '',
      incomingEnabled: Boolean(account.incomingEnabled),
      outgoingEnabled: Boolean(account.outgoingEnabled),
    });
  };

  const set = (key, value) => setForm((curr) => ({ ...curr, [key]: value }));

  const reveal = async (id) => {
    const secret = await onReveal(id);
    setRevealed((curr) => ({ ...curr, [id]: secret }));
  };

  return (
    <div className="ka-stack">
      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Cuentas corporativas</h2>
            <p>Buzones creados en Zimbra y preparados para webmail, SMTP e ingestion de entradas.</p>
          </div>
          <div className="ka-card-actions">
            <a className="ka-btn ka-btn--soft" href="https://helpmail.weltascorp.com" target="_blank" rel="noreferrer">Guia de configuracion</a>
            <a className="ka-btn ka-btn--soft" href="https://mail.weltas.es:444/" target="_blank" rel="noreferrer">Abrir webmail</a>
          </div>
        </div>
        <div className="ka-mail-grid">
          {accounts.map((account) => (
            <article key={account._id} className="ka-mail-card">
              <header>
                <div>
                  <strong>{account.email}</strong>
                  <span>{account.label || account.localPart} · {account.provider}</span>
                </div>
                <span className={`ka-dot ka-dot--${account.status}`} />
              </header>
              {editing === account._id ? (
                <div className="ka-form">
                  <div className="ka-form-grid ka-form-grid--mail">
                    <label><span>Etiqueta</span><input value={form.label} onChange={(e) => set('label', e.target.value)} /></label>
                    <label><span>Estado</span><select value={form.status} onChange={(e) => set('status', e.target.value)}><option value="active">Activa</option><option value="created">Creada</option><option value="paused">Pausada</option></select></label>
                  </div>
                  <label><span>Proposito</span><textarea rows={2} value={form.purpose} onChange={(e) => set('purpose', e.target.value)} /></label>
                  <div className="ka-checks">
                    <label><input type="checkbox" checked={form.incomingEnabled} onChange={(e) => set('incomingEnabled', e.target.checked)} /> Entrada</label>
                    <label><input type="checkbox" checked={form.outgoingEnabled} onChange={(e) => set('outgoingEnabled', e.target.checked)} /> Salida</label>
                    <label><input type="checkbox" checked={form.autoReplyEnabled} onChange={(e) => set('autoReplyEnabled', e.target.checked)} /> Auto-respuesta</label>
                  </div>
                  <label><span>Texto auto-respuesta</span><textarea rows={3} value={form.autoReplyTemplate} onChange={(e) => set('autoReplyTemplate', e.target.value)} /></label>
                  <div className="ka-form-actions">
                    <span />
                    <button type="button" className="ka-btn ka-btn--ghost" onClick={() => setEditing('')}>Cancelar</button>
                    <button type="button" className="ka-btn ka-btn--primary" onClick={async () => { await onSave(account._id, form); setEditing(''); }}>Guardar</button>
                  </div>
                </div>
              ) : (
                <>
                  <p>{account.purpose || 'Sin proposito definido.'}</p>
                  <dl className="ka-mail-meta">
                    <div><dt>Entrada</dt><dd>{account.incomingEnabled ? 'Activa' : 'Desactivada'}</dd></div>
                    <div><dt>Salida</dt><dd>{account.outgoingEnabled ? 'Activa' : 'Desactivada'}</dd></div>
                    <div><dt>Auto</dt><dd>{account.autoReplyEnabled ? 'Activa' : 'No'}</dd></div>
                    <div><dt>Inbox</dt><dd>{account.inbox?.unreadCount || 0} sin leer</dd></div>
                  </dl>
                  <div className="ka-secret-row">
                    <div>
                      <span>Credencial</span>
                      <strong>{revealed[account._id] || (account.credential?.hasSecret ? 'Guardada' : 'Pendiente')}</strong>
                    </div>
                    {account.credential?.hasSecret && (
                      <button type="button" className="ka-btn ka-btn--soft" onClick={() => reveal(account._id)}><Icon name="key" />Revelar</button>
                    )}
                  </div>
                  <div className="ka-card-actions">
                    <button type="button" className="ka-btn ka-btn--soft" onClick={() => startEdit(account)}>Editar</button>
                    <a className="ka-btn ka-btn--ghost" href={account.loginUrl || 'https://mail.weltas.es:444/'} target="_blank" rel="noreferrer">Acceder</a>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function MailNotificationsSection({ notifications, accounts, onStatus, onAckAll }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [email, setEmail] = useState('all');
  const [selected, setSelected] = useState('');

  const filtered = useMemo(() => notifications.filter((item) => {
    if (status !== 'all' && item.status !== status) return false;
    if (email !== 'all' && item.email !== email) return false;
    return includesText([item.email, item.fromName, item.fromAddress, item.subject, item.snippet, item.bodyText], query);
  }), [notifications, query, status, email]);

  const current = selected ? filtered.find((item) => item._id === selected) || notifications.find((item) => item._id === selected) : filtered[0];
  const newCount = notifications.filter((item) => item.status === 'new').length;

  return (
    <div className="ka-stack">
      <section className="ka-tools">
        <div className="ka-search">
          <Icon name="search" />
          <input type="search" placeholder="Buscar remitente, asunto o texto" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Filtrar por buzon">
          <option value="all">Todos los buzones</option>
          {accounts.map((account) => <option key={account.email} value={account.email}>{account.email}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filtrar por estado">
          <option value="all">Todos los estados</option>
          {MAIL_STATES.map((value) => <option key={value} value={value}>{MAIL_LABELS[value]}</option>)}
        </select>
        <button type="button" className="ka-btn ka-btn--soft" onClick={onAckAll} disabled={!newCount}>Marcar leidas</button>
      </section>

      <section className="ka-master-detail ka-master-detail--wide">
        <div className="ka-panel">
          <div className="ka-panel-head">
            <div>
              <h2>Bandeja centralizada</h2>
              <p>{filtered.length} correos mostrados · {newCount} nuevos.</p>
            </div>
          </div>
          <div className="ka-ticket-list">
            {filtered.length === 0 ? <EmptyState title="Sin correos" body="Cuando lleguen mensajes a los buzones configurados apareceran aqui." /> : filtered.map((item) => (
              <button key={item._id} className={current?._id === item._id ? 'is-active' : ''} onClick={() => setSelected(item._id)}>
                <strong>{item.subject || '(sin asunto)'}</strong>
                <span>{item.fromName || item.fromAddress || 'Remitente desconocido'} · {item.email}</span>
                <span>{formatDate(item.receivedAt, true)}</span>
                <StatusPill value={item.status} mail />
              </button>
            ))}
          </div>
        </div>

        <div className="ka-panel">
          {current ? (
            <article className="ka-mail-detail">
              <div className="ka-panel-head">
                <div>
                  <p className="ka-kicker">{current.email}</p>
                  <h2>{current.subject || '(sin asunto)'}</h2>
                  <p>{formatDate(current.receivedAt, true)}</p>
                </div>
                <StatusPill value={current.status} mail />
              </div>
              <div className="ka-detail-grid">
                <div><span>De</span><strong>{current.fromName || current.fromAddress || '-'}</strong></div>
                <div><span>Email</span><strong>{current.fromAddress || '-'}</strong></div>
                <div><span>Auto-respuesta</span><strong>{current.autoReplySent ? 'Enviada' : 'No enviada'}</strong></div>
                <div><span>ID Zimbra</span><strong>{current.zimbraId}</strong></div>
              </div>
              {current.snippet && <p className="ka-note"><b>Resumen:</b> {current.snippet}</p>}
              <pre className="ka-mail-body">{current.bodyText || 'Sin cuerpo de mensaje disponible.'}</pre>
              <div className="ka-form-actions">
                <span />
                {MAIL_STATES.map((value) => (
                  <button key={value} type="button" className="ka-btn ka-btn--soft" onClick={() => onStatus(current._id, value)}>{MAIL_LABELS[value]}</button>
                ))}
              </div>
            </article>
          ) : (
            <EmptyState title="Selecciona un correo" body="El detalle aparecera aqui." />
          )}
        </div>
      </section>
    </div>
  );
}

function CatalogSection({ products, categories, onSaveProduct, onDeleteProduct, onSaveCategory, token }) {
  const [tab, setTab] = useState('products');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  const categoryMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.slug, c.nombre])), [categories]);
  const filteredProducts = useMemo(() => products.filter((p) => {
    if (category !== 'all' && p.categoria !== category) return false;
    return includesText([p.nombre, p.slug, p.categoria, p.descripcion, ...(p.alergenos || [])], query);
  }), [products, category, query]);

  return (
    <div className="ka-stack">
      <section className="ka-tools">
        <div className="ka-segmented">
          <button className={tab === 'products' ? 'is-active' : ''} onClick={() => setTab('products')}>Productos</button>
          <button className={tab === 'categories' ? 'is-active' : ''} onClick={() => setTab('categories')}>Categorias</button>
        </div>
        {tab === 'products' && (
          <>
            <div className="ka-search"><Icon name="search" /><input type="search" placeholder="Buscar producto" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="all">Todas las categorias</option>
              {categories.map((cat) => <option key={cat.slug} value={cat.slug}>{cat.nombre}</option>)}
            </select>
            <button className="ka-btn ka-btn--primary" onClick={() => setEditingProduct({ ...EMPTY_PRODUCT, categoria: categories[0]?.slug || '' })}><Icon name="plus" />Producto</button>
          </>
        )}
        {tab === 'categories' && (
          <button className="ka-btn ka-btn--primary" onClick={() => setEditingCategory({ ...EMPTY_CATEGORY })}><Icon name="plus" />Categoria</button>
        )}
      </section>

      {tab === 'products' ? (
        <section className="ka-product-grid">
          {filteredProducts.map((product) => (
            <article key={product._id} className={product.activo ? 'ka-product-card' : 'ka-product-card is-muted'}>
              <div className="ka-product-image">
                {product.imagenes?.[0] ? <img src={product.imagenes[0]} alt={product.nombre} /> : <span>{product.nombre.slice(0, 2).toUpperCase()}</span>}
              </div>
              <div className="ka-product-body">
                <div>
                  <h3>{product.nombre}</h3>
                  <p>{categoryMap[product.categoria] || product.categoria}</p>
                </div>
                <strong>{formatMoney(product.precioBase)}</strong>
                <div className="ka-tags">
                  {product.destacado && <span>Destacado</span>}
                  {!product.activo && <span>Pausado</span>}
                  {(product.alergenos || []).slice(0, 3).map((a) => <span key={a}>{a}</span>)}
                </div>
                <button className="ka-btn ka-btn--soft" onClick={() => setEditingProduct(product)}><Icon name="edit" />Editar</button>
              </div>
            </article>
          ))}
          {filteredProducts.length === 0 && <EmptyState title="Sin productos" body="Ajusta los filtros o crea un producto nuevo." />}
        </section>
      ) : (
        <section className="ka-panel">
          <div className="ka-table">
            {categories.map((cat) => (
              <button key={cat._id} type="button" className="ka-table-row" onClick={() => setEditingCategory(cat)}>
                <span><strong>{cat.nombre}</strong><small>{cat.slug}</small></span>
                <span>{cat.descripcion || 'Sin descripcion'}</span>
                <span>{cat.activo ? 'Activa' : 'Pausada'}</span>
                <span className="ka-align-right">Orden {cat.orden}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {editingProduct && (
        <ProductEditor
          product={editingProduct}
          categories={categories}
          token={token}
          onClose={() => setEditingProduct(null)}
          onSave={async (payload) => { await onSaveProduct(payload); setEditingProduct(null); }}
          onDelete={editingProduct._id ? async () => { await onDeleteProduct(editingProduct._id); setEditingProduct(null); } : null}
        />
      )}
      {editingCategory && (
        <CategoryEditor
          category={editingCategory}
          token={token}
          onClose={() => setEditingCategory(null)}
          onSave={async (payload) => { await onSaveCategory(payload); setEditingCategory(null); }}
        />
      )}
    </div>
  );
}

// Gestor de imágenes: subir desde el equipo, reutilizar de la biblioteca o pegar una URL.
function ImagesField({ value = [], onChange, token, multiple = true, label = 'Imagenes' }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [libOpen, setLibOpen] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const list = Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : []);
  const commit = (next) => onChange(multiple ? next : next.slice(-1));
  const addUrls = (urls) => {
    const clean = urls.map((u) => String(u).trim()).filter(Boolean);
    commit(multiple ? [...list, ...clean.filter((u) => !list.includes(u))] : clean.slice(-1));
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).slice(0, 10).forEach((f) => fd.append('files', f));
      const { files: saved } = await api.adminUpload(fd, token);
      addUrls(saved.map((s) => s.url));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeAt = (i) => commit(list.filter((_, idx) => idx !== i));
  const makeMain = (i) => { const next = [...list]; const [it] = next.splice(i, 1); next.unshift(it); commit(next); };

  return (
    <div className="ka-imgfield">
      <span className="ka-imgfield-label">
        {label}{multiple && list.length > 1 ? ` · ${list.length} imagenes (la 1ª es la principal)` : ''}
      </span>
      <div className="ka-imggrid">
        {list.map((url, i) => (
          <div className={'ka-imgcard' + (i === 0 ? ' is-main' : '')} key={url + i}>
            <img src={url} alt="" loading="lazy" />
            {i === 0 && multiple && <span className="ka-imgbadge">Principal</span>}
            <div className="ka-imgcard-actions">
              {multiple && i > 0 && <button type="button" title="Hacer principal" onClick={() => makeMain(i)}>★</button>}
              <button type="button" title="Quitar de este producto" onClick={() => removeAt(i)}>✕</button>
            </div>
          </div>
        ))}
        <button type="button" className="ka-imgadd" onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? 'Subiendo…' : '＋ Subir'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple={multiple} hidden onChange={(e) => upload(e.target.files)} />
      <div className="ka-imgfield-tools">
        <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={() => setLibOpen(true)}>Biblioteca</button>
        <div className="ka-imgurl">
          <input placeholder="…o pega una URL" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (urlDraft.trim()) { addUrls([urlDraft]); setUrlDraft(''); } } }} />
          <button type="button" className="ka-btn ka-btn--soft ka-btn--sm" onClick={() => { if (urlDraft.trim()) { addUrls([urlDraft]); setUrlDraft(''); } }}>Anadir</button>
        </div>
      </div>
      {err && <p className="ka-alert ka-alert--danger" role="alert">{err}</p>}
      {libOpen && <MediaLibrary token={token} selected={list} onPick={(url) => addUrls([url])} onClose={() => setLibOpen(false)} />}
    </div>
  );
}

// Biblioteca: lista todas las imágenes subidas para reutilizarlas o borrarlas del servidor.
function MediaLibrary({ token, selected = [], onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.adminMedia(token).then((d) => { if (alive) setItems(d); }).catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [token]);

  const remove = async (name) => {
    if (!window.confirm('¿Eliminar esta imagen del servidor? Si algún producto la usa, dejará de verse.')) return;
    try { await api.adminMediaDelete(name, token); setItems((c) => c.filter((x) => x.nombre !== name)); }
    catch (e) { setErr(e.message); }
  };

  return (
    <Modal title="Biblioteca de imagenes" onClose={onClose}>
      {err && <p className="ka-alert ka-alert--danger" role="alert">{err}</p>}
      {items === null ? <p className="ka-muted">Cargando…</p>
        : items.length === 0 ? <p className="ka-muted">Aún no hay imágenes subidas. Usa “＋ Subir” en el formulario para añadir las primeras.</p>
        : (
          <div className="ka-medialib">
            {items.map((it) => (
              <div className={'ka-mediacard' + (selected.includes(it.url) ? ' is-sel' : '')} key={it.nombre}>
                <img src={it.url} alt={it.nombre} loading="lazy" onClick={() => onPick(it.url)} title="Usar esta imagen" />
                <div className="ka-mediacard-bar">
                  <button type="button" onClick={() => onPick(it.url)}>Usar</button>
                  <button type="button" className="del" onClick={() => remove(it.nombre)} title="Eliminar del servidor">🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </Modal>
  );
}

function ProductEditor({ product, categories, onClose, onSave, onDelete, token }) {
  const [form, setForm] = useState(() => ({
    ...product,
    imagenes: Array.isArray(product.imagenes) ? product.imagenes : [],
    alergenosText: arrayToText(product.alergenos),
    opcionesText: prettyJson(product.opciones),
  }));
  const [error, setError] = useState('');
  const set = (key, value) => setForm((curr) => ({ ...curr, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const opciones = JSON.parse(form.opcionesText || '[]');
      if (!Array.isArray(opciones)) throw new Error('Las opciones deben ser un array JSON.');
      const { alergenosText, opcionesText, createdAt, updatedAt, __v, ...productFields } = form;
      await onSave({
        ...productFields,
        precioBase: Number(form.precioBase) || 0,
        maxCaracteresTexto: Number(form.maxCaracteresTexto) || 0,
        orden: Number(form.orden) || 0,
        imagenes: Array.isArray(form.imagenes) ? form.imagenes : [],
        alergenos: textToArray(alergenosText),
        opciones,
      });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Modal title={product._id ? 'Editar producto' : 'Nuevo producto'} onClose={onClose}>
      <form className="ka-form" onSubmit={submit}>
        <div className="ka-form-grid">
          <label><span>Nombre</span><input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} required /></label>
          <label><span>Slug</span><input value={form.slug} onChange={(e) => set('slug', e.target.value)} required /></label>
          <label><span>Categoria</span><select value={form.categoria} onChange={(e) => set('categoria', e.target.value)}>{categories.map((c) => <option key={c.slug} value={c.slug}>{c.nombre}</option>)}</select></label>
          <label><span>Precio base</span><input type="number" step="0.01" value={form.precioBase} onChange={(e) => set('precioBase', e.target.value)} /></label>
          <label><span>Orden</span><input type="number" value={form.orden} onChange={(e) => set('orden', e.target.value)} /></label>
          <label><span>Max. caracteres</span><input type="number" value={form.maxCaracteresTexto} onChange={(e) => set('maxCaracteresTexto', e.target.value)} /></label>
        </div>
        <label><span>Descripcion</span><textarea rows={4} value={form.descripcion} onChange={(e) => set('descripcion', e.target.value)} /></label>
        <ImagesField label="Imagenes del producto" value={form.imagenes} onChange={(v) => set('imagenes', v)} token={token} multiple />
        <label><span>Alergenos (separados por coma o linea)</span><textarea rows={2} value={form.alergenosText} onChange={(e) => set('alergenosText', e.target.value)} /></label>
        <label><span>Opciones del configurador (JSON)</span><textarea className="ka-code" rows={9} value={form.opcionesText} onChange={(e) => set('opcionesText', e.target.value)} /></label>
        <div className="ka-checks">
          <label><input type="checkbox" checked={form.activo} onChange={(e) => set('activo', e.target.checked)} /> Activo en carta</label>
          <label><input type="checkbox" checked={form.destacado} onChange={(e) => set('destacado', e.target.checked)} /> Destacado</label>
          <label><input type="checkbox" checked={form.permitePersonalizarTexto} onChange={(e) => set('permitePersonalizarTexto', e.target.checked)} /> Permite texto personalizado</label>
        </div>
        {error && <p className="ka-alert ka-alert--danger" role="alert">{error}</p>}
        <div className="ka-form-actions">
          {onDelete && <button type="button" className="ka-btn ka-btn--danger" onClick={onDelete}>Eliminar</button>}
          <span />
          <button type="button" className="ka-btn ka-btn--ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="ka-btn ka-btn--primary">Guardar</button>
        </div>
      </form>
    </Modal>
  );
}

function CategoryEditor({ category, onClose, onSave, token }) {
  const [form, setForm] = useState(category);
  const set = (key, value) => setForm((curr) => ({ ...curr, [key]: value }));
  return (
    <Modal title={category._id ? 'Editar categoria' : 'Nueva categoria'} onClose={onClose}>
      <form className="ka-form" onSubmit={(e) => { e.preventDefault(); onSave({ ...form, orden: Number(form.orden) || 0 }); }}>
        <div className="ka-form-grid">
          <label><span>Nombre</span><input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} required /></label>
          <label><span>Slug</span><input value={form.slug} onChange={(e) => set('slug', e.target.value)} required /></label>
          <label><span>Orden</span><input type="number" value={form.orden} onChange={(e) => set('orden', e.target.value)} /></label>
        </div>
        <label><span>Descripcion</span><textarea rows={3} value={form.descripcion} onChange={(e) => set('descripcion', e.target.value)} /></label>
        <ImagesField label="Imagen de la categoria" value={form.imagen} onChange={(v) => set('imagen', Array.isArray(v) ? (v[0] || '') : (v || ''))} token={token} multiple={false} />
        <div className="ka-checks"><label><input type="checkbox" checked={form.activo} onChange={(e) => set('activo', e.target.checked)} /> Activa</label></div>
        <div className="ka-form-actions">
          <span />
          <button type="button" className="ka-btn ka-btn--ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="ka-btn ka-btn--primary">Guardar</button>
        </div>
      </form>
    </Modal>
  );
}

function CalendarSection({ orders, settings, onSave }) {
  const [form, setForm] = useState(() => settingsToForm(settings));
  useEffect(() => { setForm(settingsToForm(settings)); }, [settings]);

  const days = useMemo(() => {
    const out = [];
    const closed = new Set((settings?.diasCerrado || []).map(Number));
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const key = ymd(date);
      out.push({
        key,
        label: formatDate(date),
        closed: closed.has(date.getDay()),
        orders: orders.filter((o) => ymd(o.fechaEntrega) === key && o.estado !== 'cancelado'),
      });
    }
    return out;
  }, [orders, settings]);

  const save = () => onSave(formToSettings(form, settings));

  return (
    <div className="ka-grid ka-grid--calendar">
      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Proximos 14 dias</h2>
            <p>Vista rapida de carga de trabajo por fecha de entrega.</p>
          </div>
        </div>
        <div className="ka-calendar-list">
          {days.map((day) => (
            <article key={day.key} className={day.closed ? 'is-closed' : ''}>
              <div>
                <strong>{day.label}</strong>
                <span>{day.closed ? 'Cerrado' : `${day.orders.length} pedidos`}</span>
              </div>
              <div className="ka-mini-bars">
                {(settings?.ventanas || ['10-12', '12-14', '17-19']).map((window) => {
                  const count = day.orders.filter((o) => o.ventana === window).length;
                  return <span key={window} title={`${window}: ${count}`} style={{ '--fill': Math.min(100, count * 34) + '%' }} />;
                })}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Capacidad y fechas</h2>
            <p>Reglas usadas por checkout para fechas disponibles.</p>
          </div>
        </div>
        <SettingsForm form={form} setForm={setForm} onSave={save} />
      </section>
    </div>
  );
}

function settingsToForm(settings) {
  return {
    pedidoMinimo: settings?.pedidoMinimo ?? 15,
    antelacionEstandar: settings?.antelacionHoras?.estandar ?? 48,
    antelacionPersonalizado: settings?.antelacionHoras?.personalizado ?? 72,
    ventanasText: (settings?.ventanas || []).join('\n'),
    tarifaEnvio: settings?.tarifaEnvio ?? 5,
    envioGratisDesde: settings?.envioGratisDesde ?? 60,
    zonasEnvioText: (settings?.zonasEnvio || []).join('\n'),
    diasCerrado: settings?.diasCerrado || [1],
  };
}

function formToSettings(form, current) {
  return {
    ...current,
    key: 'global',
    pedidoMinimo: Number(form.pedidoMinimo) || 0,
    antelacionHoras: {
      estandar: Number(form.antelacionEstandar) || 0,
      personalizado: Number(form.antelacionPersonalizado) || 0,
    },
    ventanas: textToArray(form.ventanasText),
    tarifaEnvio: Number(form.tarifaEnvio) || 0,
    envioGratisDesde: Number(form.envioGratisDesde) || 0,
    zonasEnvio: textToArray(form.zonasEnvioText),
    diasCerrado: (form.diasCerrado || []).map(Number),
  };
}

function SettingsForm({ form, setForm, onSave }) {
  const set = (key, value) => setForm((curr) => ({ ...curr, [key]: value }));
  const toggleClosedDay = (day) => {
    const current = new Set((form.diasCerrado || []).map(Number));
    if (current.has(day)) current.delete(day);
    else current.add(day);
    set('diasCerrado', [...current].sort((a, b) => a - b));
  };
  const days = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

  return (
    <div className="ka-form">
      <div className="ka-form-grid">
        <label><span>Pedido minimo</span><input type="number" value={form.pedidoMinimo} onChange={(e) => set('pedidoMinimo', e.target.value)} /></label>
        <label><span>Antelacion estandar (h)</span><input type="number" value={form.antelacionEstandar} onChange={(e) => set('antelacionEstandar', e.target.value)} /></label>
        <label><span>Antelacion personalizados (h)</span><input type="number" value={form.antelacionPersonalizado} onChange={(e) => set('antelacionPersonalizado', e.target.value)} /></label>
        <label><span>Tarifa envio</span><input type="number" step="0.01" value={form.tarifaEnvio} onChange={(e) => set('tarifaEnvio', e.target.value)} /></label>
        <label><span>Envio gratis desde</span><input type="number" step="0.01" value={form.envioGratisDesde} onChange={(e) => set('envioGratisDesde', e.target.value)} /></label>
      </div>
      <label><span>Ventanas horarias (una por linea)</span><textarea rows={4} value={form.ventanasText} onChange={(e) => set('ventanasText', e.target.value)} /></label>
      <label><span>Zonas de envio (una por linea)</span><textarea rows={3} value={form.zonasEnvioText} onChange={(e) => set('zonasEnvioText', e.target.value)} /></label>
      <div className="ka-field">
        <span>Dias cerrados</span>
        <div className="ka-day-toggles">
          {days.map((label, day) => (
            <button key={label} type="button" className={(form.diasCerrado || []).includes(day) ? 'is-active' : ''} onClick={() => toggleClosedDay(day)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="ka-form-actions">
        <span />
        <button type="button" className="ka-btn ka-btn--primary" onClick={onSave}>Guardar ajustes</button>
      </div>
    </div>
  );
}

function SettingsSection({ settings, onSave }) {
  const [form, setForm] = useState(() => settingsToForm(settings));
  useEffect(() => { setForm(settingsToForm(settings)); }, [settings]);

  return (
    <div className="ka-grid ka-grid--settings">
      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Reglas comerciales</h2>
            <p>Cambios con efecto directo sobre el proceso de pedido.</p>
          </div>
        </div>
        <SettingsForm form={form} setForm={setForm} onSave={() => onSave(formToSettings(form, settings))} />
      </section>

      <section className="ka-panel">
        <div className="ka-panel-head">
          <div>
            <h2>Contacto publico</h2>
            <p>Estos datos vienen del entorno del backend.</p>
          </div>
        </div>
        <dl className="ka-defs">
          <div><dt>WhatsApp</dt><dd>{settings?.whatsapp || 'Pendiente en .env'}</dd></div>
          <div><dt>Telefono</dt><dd>{settings?.telefono || 'Pendiente en .env'}</dd></div>
          <div><dt>Email</dt><dd>{settings?.email || 'Pendiente en .env'}</dd></div>
        </dl>
        <p className="ka-note">Para no exponer secretos ni mezclar configuracion operativa con credenciales, el contacto publico se mantiene en variables de entorno del backend.</p>
      </section>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="ka-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="ka-modal" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button type="button" className="ka-icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="close" /></button>
        </header>
        {children}
      </section>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('kanel_admin') || '');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [mailNotifications, setMailNotifications] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);

  const logout = () => {
    localStorage.removeItem('kanel_admin');
    setToken('');
  };

  const login = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await api.login(pass);
      localStorage.setItem('kanel_admin', response.token);
      setToken(response.token);
      setPass('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [nextOrders, nextCustoms, nextInvoices, nextEmailAccounts, nextMailNotifications, nextProducts, nextCategories, nextSettings] = await Promise.all([
        api.adminGet('/orders', token),
        api.adminGet('/custom-orders', token),
        api.adminGet('/invoices', token),
        api.adminGet('/email-accounts', token),
        api.adminGet('/mail-notifications', token),
        api.adminGet('/products', token),
        api.adminGet('/categories', token),
        api.adminGet('/settings', token),
      ]);
      setOrders(nextOrders);
      setCustoms(nextCustoms);
      setInvoices(nextInvoices);
      setEmailAccounts(nextEmailAccounts);
      setMailNotifications(nextMailNotifications);
      setProducts(nextProducts);
      setCategories(nextCategories);
      setSettings(nextSettings);
    } catch (err) {
      if (String(err.message).toLowerCase().includes('autoriz')) logout();
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2400);
  };

  const updateOrderStatus = async (id, estado) => {
    await api.adminSend(`/orders/${id}`, 'PATCH', { estado }, token);
    flash('Pedido actualizado');
    await load();
  };

  const updateCustomStatus = async (id, estado) => {
    await api.adminSend(`/custom-orders/${id}`, 'PATCH', { estado }, token);
    flash('Solicitud actualizada');
    await load();
  };

  const generateInvoice = async (orderId) => {
    await api.adminSend(`/invoices/from-order/${orderId}`, 'POST', {}, token);
    flash('Factura generada');
    await load();
    setActive('invoices');
  };

  const updateInvoice = async (id, payload) => {
    await api.adminSend(`/invoices/${id}`, 'PATCH', payload, token);
    flash('Factura actualizada');
    await load();
  };

  const updateEmailAccount = async (id, payload) => {
    await api.adminSend(`/email-accounts/${id}`, 'PATCH', payload, token);
    flash('Cuenta actualizada');
    await load();
  };

  const revealEmailSecret = async (id) => {
    const result = await api.adminSend(`/email-accounts/${id}/reveal`, 'POST', {}, token);
    flash('Credencial revelada');
    return result.secret;
  };

  const updateMailNotification = async (id, status) => {
    await api.adminSend(`/mail-notifications/${id}`, 'PATCH', { status }, token);
    flash('Notificacion actualizada');
    await load();
  };

  const ackAllMailNotifications = async () => {
    await api.adminSend('/mail-notifications/ack-all', 'POST', {}, token);
    flash('Notificaciones marcadas');
    await load();
  };

  const saveProduct = async (payload) => {
    if (payload._id) await api.adminSend(`/products/${payload._id}`, 'PUT', payload, token);
    else await api.adminSend('/products', 'POST', payload, token);
    flash('Producto guardado');
    await load();
  };

  const deleteProduct = async (id) => {
    await api.adminSend(`/products/${id}`, 'DELETE', undefined, token);
    flash('Producto eliminado');
    await load();
  };

  const saveCategory = async (payload) => {
    if (payload._id) await api.adminSend(`/categories/${payload._id}`, 'PUT', payload, token);
    else await api.adminSend('/categories', 'POST', payload, token);
    flash('Categoria guardada');
    await load();
  };

  const saveSettings = async (payload) => {
    const saved = await api.adminSend('/settings', 'PUT', payload, token);
    setSettings(saved);
    flash('Ajustes guardados');
  };

  if (!token) {
    return <AdminLogin pass={pass} setPass={setPass} login={login} error={error} loading={loading} />;
  }

  return (
    <Shell active={active} setActive={setActive} logout={logout} loading={loading} reload={load}>
      {error && <p className="ka-alert ka-alert--danger" role="alert">{error}</p>}
      {notice && <p className="ka-toast" role="status">{notice}</p>}
      {loading && !orders.length ? <div className="ka-loading">Cargando panel...</div> : (
        <>
          {active === 'dashboard' && <Dashboard orders={orders} customs={customs} products={products} invoices={invoices} emailAccounts={emailAccounts} mailNotifications={mailNotifications} settings={settings} setActive={setActive} />}
          {active === 'orders' && <OrdersSection orders={orders} onStatus={updateOrderStatus} />}
          {active === 'custom' && <CustomSection customs={customs} onStatus={updateCustomStatus} />}
          {active === 'invoices' && <InvoicesSection invoices={invoices} orders={orders} onGenerate={generateInvoice} onStatus={updateInvoice} />}
          {active === 'emailAccounts' && <EmailAccountsSection accounts={emailAccounts} onSave={updateEmailAccount} onReveal={revealEmailSecret} />}
          {active === 'mailNotifications' && <MailNotificationsSection notifications={mailNotifications} accounts={emailAccounts} onStatus={updateMailNotification} onAckAll={ackAllMailNotifications} />}
          {active === 'catalog' && (
            <CatalogSection
              products={products}
              categories={categories}
              token={token}
              onSaveProduct={saveProduct}
              onDeleteProduct={deleteProduct}
              onSaveCategory={saveCategory}
            />
          )}
          {active === 'calendar' && <CalendarSection orders={orders} settings={settings} onSave={saveSettings} />}
          {active === 'settings' && <SettingsSection settings={settings} onSave={saveSettings} />}
        </>
      )}
    </Shell>
  );
}
